import type { TViolationType } from "../interfaces/statues";
import { discreteAvailabilityFunctions } from "./availabilityDiscrete";
import type { IState, TAvailabilityFn } from "./helpers";

/* This calculator determines the earliest possible date for the next Hos event (could be a violation, availability etc.) by considering user-specific discrete availabilities and any other considerations. */

const getTimeUntilNextWorkdayEndHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.Workday(state, true);
};

const getTimeUntilNextWorkdayDrivingHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.Driving(state, true);
};

const getTimeUntilNextWorkdayDutyHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.Duty(state, true);
};

const getTimeUntilNextWorkdayRestHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.Rest(state, true);
};

const getTimeUntilNextCycleDutyHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.Cycle(state, true);
};

const getTimeUntilNextCycleRestHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.CycleRest(state, true);
};

const getTimeUntilNextCycleEndHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.CycleEnd(state, true);
};

const getTimeUntilNextCycleDrivingHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.CycleDriving(state, true);
};

const getTimeUntilNextCycleRestDutyHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.DutySinceCycleRest(state, true);
};

const getTimeUntilNextDailyDrivingHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.DailyDriving(state, true);
};

const getTimeUntilNextDailyDutyHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.DailyDuty(state, true);
};

const getTimeUntilNextDailyOffHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.DailyOff(state, true);
};

const getTimeUntilNextDailyRestHosEvent: TAvailabilityFn = (state: IState) => {
    return discreteAvailabilityFunctions.DailyRest(state, true);
};

export const discreteAvailabilityWithConsiderationsFunctions: { [violationType in TViolationType]: TAvailabilityFn } = {
    Workday: getTimeUntilNextWorkdayEndHosEvent,
    Driving: getTimeUntilNextWorkdayDrivingHosEvent,
    Duty: getTimeUntilNextWorkdayDutyHosEvent,
    Rest: getTimeUntilNextWorkdayRestHosEvent,
    Cycle: getTimeUntilNextCycleDutyHosEvent,
    CycleDriving: getTimeUntilNextCycleDrivingHosEvent,
    CycleRest: getTimeUntilNextCycleRestHosEvent,
    CycleEnd: getTimeUntilNextCycleEndHosEvent,
    DutySinceCycleRest: getTimeUntilNextCycleRestDutyHosEvent,
    DailyDriving: getTimeUntilNextDailyDrivingHosEvent,
    DailyDuty: getTimeUntilNextDailyDutyHosEvent,
    DailyOff: getTimeUntilNextDailyOffHosEvent,
    DailyRest: getTimeUntilNextDailyRestHosEvent
};

export const getNextHosEventDate = (state: IState): Date => {
    const availabilities = (Object.keys(discreteAvailabilityWithConsiderationsFunctions) as TViolationType[])
        .map(type => discreteAvailabilityWithConsiderationsFunctions[type](state))
        .filter((value): value is number => value !== undefined);

    let minTime = 0;
    if (availabilities.length > 0) {
        if (availabilities.some(ms => ms <= 0)) {
            minTime = 0;
        } else {
            minTime = Math.min(...availabilities);
        }
    }
    return new Date(state.currentDate.getTime() + minTime);
};
